import { Box, Divider, Heading } from '@zillow/constellation-10';
import type { GetServerSideProps } from 'next';
import Head from 'next/head';
import React from 'react';

import { SSID_INFO_KEY, SSID_INFO_URL } from '../../server/config';
import MlsDisclaimer, { type MlsData } from '../components/MlsDisclaimer';

export type { MlsData };

export interface MlsDisclaimersProps {
    data: MlsData[] | null;
    error: string;
}

/**
 * Server-side props for the MLS disclaimers page.
 * Extends MlsDisclaimersProps with additional server-side rendering configuration.
 */
interface MlsDisclaimersServerProps extends MlsDisclaimersProps {
    /** Toggles the visual refresh theme for the page layout */
    useVisualRefreshTheme: boolean;
}

// Separate the header into its own component for better testing
export const MlsHeader: React.FC = () => (
    /*
     * v8 <GridContent backgroundColor="gray100" padding="xl" marginBottom="lg">.
     * v10 has no GridContent, so a Box reproduces what it actually rendered:
     * GridContent is a Grid, and Grid emits nothing unless a grid prop is set
     * (none is here), so this was a plain block div carrying the theme's
     * GridContent.borderRadius plus a background colour.
     *
     *   gray100 (#F7F7F7)      → gray.50 (hsl(0 0% 96.86%) = #F7F7F7, exact).
     *                            NOT gray.100, which is #EEEEF0 — v8's gray200.
     *   GridContent.borderRadius 12px → radii obj.default (0.75rem, exact).
     *   padding      xl = 48px → layout.looser
     *   marginBottom lg = 32px → looser
     *   style={{ textAlign }}  → moved into `css`; it was only an inline style
     *                            because v8 GridContent had no textAlign prop.
     */
    <Box
        css={{
            backgroundColor: 'gray.50',
            borderRadius: 'obj.default',
            padding: 'layout.looser',
            marginBottom: 'looser',
            textAlign: 'center',
        }}
    >
        {/*
         * v8 fontType={{ default: 'h3', md_lte: 'h4', sm_lte: 'h5' }}. The keys
         * are max-width queries applied in insertion order, so the effective
         * bands were: ≤480px h5, 481–768px h4, ≥769px h3. Panda is mobile-first,
         * which reads them in the opposite direction — base h5, `md` (480px) h4,
         * `lg` (768px) h3. All three sizes map exactly: h5 = heading-xs
         * (20/24/900), h4 = heading-sm (24/32/900), h3 = heading-md (36/40/900).
         *
         * v8 `serif` → v10 `heading-alt`; kept as a prop because the two heading
         * families only coincide under the `zillow` theme.
         * marginBottom xs = 8px → `tighter`.
         */}
        <Heading
            level={1}
            fontFamily="heading-alt"
            css={{
                textStyle: {
                    base: 'heading-xs',
                    md: 'heading-sm',
                    lg: 'heading-md',
                },
                marginBottom: 'tighter',
            }}
        >
            MLS and Canadian Board Disclaimers
        </Heading>
    </Box>
);

// Separate the disclaimer list into its own component
export const MlsDisclaimerList: React.FC<MlsDisclaimersProps> = ({
    data,
    error,
}) => {
    if (error) return <div>{error}</div>;
    if (!data) return null;

    const sortedData = data.sort((a, b) =>
        a.longName.localeCompare(b.longName)
    );
    const lastIndex = sortedData.length - 1;

    return (
        <>
            {sortedData.map((mls, i) => (
                <React.Fragment key={mls.ssid}>
                    <MlsDisclaimer mls={mls} i={i} />
                    {mls.disclaimer && i < lastIndex && (
                        /*
                         * v8 marginY="lg" (32px) → `css` marginBlock `looser`.
                         * v10 Divider's default tone="muted" resolves to
                         * border.muted = #CDCDD3, the same colour v8 drew.
                         */
                        <Divider css={{ marginBlock: 'looser' }} />
                    )}
                </React.Fragment>
            ))}
        </>
    );
};

// Main component becomes simpler and more focused
export function MlsDisclaimers({
    data,
    error,
}: MlsDisclaimersProps): JSX.Element {
    return (
        <>
            <Head>
                <title>MLS Disclaimers - Zillow</title>
            </Head>
            <MlsHeader />
            {/*
             * v8 <PageContent marginTop="0" style={{ maxWidth: '720px' }}>.
             * v10 has no standalone PageContent, so a Box reproduces exactly
             * what PageContentMixin emitted with the SkunkWorks `Page` tokens:
             *
             *   box-sizing: content-box            (so 720px is content only)
             *   max-width: Page.maxWidth 1232px    ← overridden inline to 720px
             *   margin: Page.verticalMargin 32px auto
             *   padding-inline: 24px, dropping to 16px at v8 `sm_lte`
             *
             * `marginTop="0"` was not a no-op: PageContentMixin ends with
             * FlexMixin, which re-runs spacingMixin after the `margin` shorthand,
             * so the top margin really did collapse to 0 while the bottom kept
             * 32px. Hence marginTop 0 + marginBottom `looser` + marginInline
             * auto rather than a single shorthand.
             *
             * v8 `sm_lte` is `max-width: 480px`, so the 24px gutter began at
             * 481px. Panda's mobile-first `md` is min-width 480px — the switch
             * lands 1px earlier, inside the gutter itself.
             */}
            <Box
                css={{
                    boxSizing: 'content-box',
                    maxWidth: '720px',
                    marginTop: '0',
                    marginBottom: 'looser',
                    marginInline: 'auto',
                    paddingInline: { base: 'default', md: 'loose' },
                }}
            >
                {/*
                 * v8 <GridContent marginX="auto"> — no backgroundColor prop, so
                 * it took the theme default #FFF, and GridContent's own
                 * defaultProps padding 'sm' (16px), neither of which is visible
                 * in the JSX. Reproduced explicitly. See MlsHeader above for the
                 * borderRadius token.
                 */}
                <Box
                    css={{
                        backgroundColor: 'white',
                        borderRadius: 'obj.default',
                        padding: 'default',
                        marginInline: 'auto',
                    }}
                >
                    <MlsDisclaimerList data={data} error={error} />
                </Box>
            </Box>
        </>
    );
}

// Separate the data fetching logic into a service
export async function fetchMlsData(): Promise<{
    data: MlsData[] | null;
    error: string;
}> {
    try {
        const request = await fetch(
            `${SSID_INFO_URL}v1/ssid/types/2?disclaimerType=full`,
            {
                headers: {
                    APIKEY: SSID_INFO_KEY,
                },
            }
        );

        if (!request.ok) {
            throw new Error(
                `HTTP error! status: ${request.status} - ${request.statusText}`
            );
        }

        const data = await request.json();

        if (!Array.isArray(data)) {
            throw new Error('Invalid response format: expected an array');
        }

        return { data, error: '' };
    } catch (e) {
        console.error('Error encountered loading mls disclaimers.', e);
        return {
            data: null,
            error: 'Error loading MLS Disclaimers. If this error persists, please contact Zillow Support: https://zillow.zendesk.com/hc/en-us',
        };
    }
}

/**
 * Fetches MLS disclaimer data on each request using server-side rendering.
 * Uses fetchMlsData to retrieve disclaimer information from the SSID API.
 *
 * @returns Promise containing GetServerSidePropsResult<MlsDisclaimersServerProps>
 *          with props: data (MLS disclaimer array or null), error (error message string),
 *          and useVisualRefreshTheme (boolean for theme configuration)
 */
export const getServerSideProps: GetServerSideProps<
    MlsDisclaimersServerProps
> = async () => {
    const { data, error } = await fetchMlsData();

    return {
        props: {
            data,
            error,
            useVisualRefreshTheme: true,
        },
    };
};

export default MlsDisclaimers;
